<script setup>
import { onMounted, ref, computed } from 'vue'
import { api } from '../api'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminNav from '../components/AdminNav.vue'
const pages = ref([])
const loading = ref(true)
const error = ref('')
const deleting = ref(null)
const router = useRouter()
const { t } = useI18n()

// Separate pages into default and custom
const defaultPages = computed(() => pages.value.filter((p) => p.is_default))
const customPages = computed(() => pages.value.filter((p) => !p.is_default))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get('/pages')
    pages.value = data
  } catch {
    error.value = 'load'
  } finally {
    loading.value = false
  }
}

async function remove(id) {
  if (!confirm(t('admin.confirmDeletePage'))) return
  deleting.value = id
  try {
    await api.delete('/pages/' + id)
    pages.value = pages.value.filter((p) => p.id !== id)
  } finally {
    deleting.value = null
  }
}

function getMenuBadge(page) {
  if (!page.show_in_menu) return t('admin.notInMenu')
  if (page.menu_position === 'header') return t('admin.menuHeader')
  if (page.menu_position === 'footer') return t('admin.menuFooter')
  return t('admin.menuNone')
}

onMounted(load)
</script>
<template>
  <section>
    <h2>{{ t('admin.pages') }}</h2>
    <AdminNav />
    <div class="action-bar">
      <button class="btn-primary" @click="router.push('/admin/pages/new')">
        + {{ t('common.newPage') }}
      </button>
      <button class="btn" @click="load" :disabled="loading">{{ t('common.refresh') }}</button>
    </div>
    <p v-if="loading">{{ t('common.loading') }}</p>
    <p v-else-if="error" style="color: #c00">{{ t('common.errorLoading') }}</p>
    <div v-else>
      <!-- Default Pages Section -->
      <div v-if="defaultPages.length" class="page-section">
        <h3 class="section-title">
          <span class="badge badge-default">{{ t('admin.defaultPages') }}</span>
        </h3>
        <div class="table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>{{ t('common.title') }}</th>
                <th>{{ t('admin.slug') }}</th>
                <th>{{ t('admin.menuPlacement') }}</th>
                <th>{{ t('admin.updated') }}</th>
                <th style="width: 120px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in defaultPages" :key="p.id">
                <td>{{ p.title }}</td>
                <td>
                  <router-link :to="'/page/' + p.slug">{{ p.slug }}</router-link>
                </td>
                <td>
                  <span class="menu-badge" :class="'badge-' + (p.menu_position || 'none')">{{
                    getMenuBadge(p)
                  }}</span>
                </td>
                <td>{{ new Date(p.updated_at).toLocaleString() }}</td>
                <td class="cell-actions">
                  <button
                    class="btn"
                    :disabled="p.slug === 'gallery'"
                    @click="router.push('/admin/pages/' + p.id)"
                    :title="p.slug === 'gallery' ? t('admin.managedElsewhere') : ''"
                  >
                    {{ t('common.edit') }}
                  </button>
                  <button
                    class="btn btn-danger"
                    :disabled="
                      deleting === p.id ||
                      ['home', 'about', 'contact', 'appearances', 'gallery'].includes(p.slug)
                    "
                    @click="remove(p.id)"
                  >
                    {{ t('common.delete') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Custom Pages Section -->
      <div v-if="customPages.length" class="page-section">
        <h3 class="section-title">
          <span class="badge badge-custom">{{ t('admin.customPages') }}</span>
        </h3>
        <div class="table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>{{ t('common.title') }}</th>
                <th>{{ t('admin.slug') }}</th>
                <th>{{ t('admin.menuPlacement') }}</th>
                <th>{{ t('admin.updated') }}</th>
                <th style="width: 120px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in customPages" :key="p.id" class="custom-page-row">
                <td>{{ p.title }}</td>
                <td>
                  <router-link :to="'/page/' + p.slug">{{ p.slug }}</router-link>
                </td>
                <td>
                  <span class="menu-badge" :class="'badge-' + (p.menu_position || 'none')">{{
                    getMenuBadge(p)
                  }}</span>
                </td>
                <td>{{ new Date(p.updated_at).toLocaleString() }}</td>
                <td class="cell-actions">
                  <button class="btn" @click="router.push('/admin/pages/' + p.id)">
                    {{ t('common.edit') }}
                  </button>
                  <button
                    class="btn btn-danger"
                    :disabled="deleting === p.id"
                    @click="remove(p.id)"
                  >
                    {{ t('common.delete') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p v-if="!defaultPages.length && !customPages.length" style="opacity: 0.7; margin-top: 2rem">
        {{ t('admin.noPagesYet') }}
      </p>
    </div>
  </section>
</template>
<style scoped>
.page-section {
  margin-top: 2rem;
}
.page-section:first-of-type {
  margin-top: 0;
}
.section-title {
  font-size: 1.1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.badge-default {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}
.badge-custom {
  background: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
}
.menu-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
}
.menu-badge.badge-header {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.3);
}
.menu-badge.badge-footer {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.3);
}
.menu-badge.badge-none {
  background: rgba(107, 114, 128, 0.2);
  color: #9ca3af;
  border: 1px solid rgba(107, 114, 128, 0.3);
}
.custom-page-row {
  background: rgba(139, 92, 246, 0.05);
}
</style>
